import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, Monitor, Smartphone, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const msg = (e, fb) => { const d = e?.response?.data?.detail; return (d && (d.message || d)) || fb; };
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

// /a/<token> — a person's own link (docs/OL_ACCESS_LINKS.md). Nothing happens until they tap:
// mail scanners that open links never sign anyone in. "Use on this device" remembers this
// browser; "Add to my club app" gives a short code to type in the app (iPhone opens links in
// Safari, not in the installed app). The token leaves the address bar right away.
export default function AccessLinkPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);

  useEffect(() => {
    try { window.history.replaceState({}, '', '/a'); } catch { /* ignore */ }
    axios.post(`${API_URL}/api/access/peek`, { token })
      .then(r => setInfo(r.data)).catch(e => setErr(msg(e, 'This link could not be used.')));
  }, [token]);

  const signIn = async (website) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/access/redeem`, { token }, { withCredentials: true });
      window.location.replace(website ? `${info.site_url}/wp-admin/admin-post.php?action=ol_sso_start` : (data.next || '/dashboard'));
    }
    catch (e) { setErr(msg(e, 'This link could not be used.')); setBusy(false); }
  };
  const appCode = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API_URL}/api/access/connect-code`, { token }); setCode(data); }
    catch (e) { setErr(msg(e, 'Could not make a code.')); }
    finally { setBusy(false); }
  };

  const b = info?.brand || {};
  const brand = b.primary_color || '#334155';
  const btn = 'w-full h-12 rounded-lg font-semibold flex items-center justify-center gap-2';
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 flex items-start sm:items-center justify-center" data-testid="access-link-page">
      <div className="max-w-sm w-full space-y-3">
        {b.org_name && (
          <div className="flex items-center gap-3 rounded-xl px-4 py-3 text-white" style={{ background: brand }}>
            {b.logo_url && <img src={b.logo_url.startsWith('http') ? b.logo_url : `${API_URL}${b.logo_url}`} alt="" className="h-10 w-10 rounded bg-white/90 object-contain p-0.5" />}
            <div className="font-bold">{b.org_name}</div>
          </div>
        )}
        <div className="rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700 p-5 space-y-3 text-center">
          {err ? (<>
            <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
            <p className="font-semibold" data-testid="access-link-err">{err}</p>
          </>) : !info ? (
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
          ) : code ? (<>
            <p className="text-sm">Open your {b.org_name || 'club'} app, tap <b>Connect</b>, and enter:</p>
            <p className="text-3xl font-bold tracking-widest" data-testid="access-connect-code">{code.code}</p>
            <p className="text-xs text-slate-500">Works for {Math.round(code.expires_in / 60)} minutes, once. Don't have the app yet? Install it first, then enter the code.</p>
            {info.app_url && !isIOS() && <a className="underline text-sm" href={`${info.app_url.replace(/\/$/, '')}/connect?t=${encodeURIComponent(token)}`}>Or open the app directly</a>}
          </>) : (<>
            <h1 className="text-lg font-bold">Hi {info.first_name || 'there'}!</h1>
            <p className="text-sm text-slate-500">This is your personal link — no password needed.</p>
            <button className={`${btn} text-white`} style={{ background: brand }} onClick={() => signIn(false)} disabled={busy} data-testid="access-use-here">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Monitor className="h-4 w-4" />}Sign in on this device</button>
            <button className={`${btn} border dark:border-slate-600`} onClick={appCode} disabled={busy} data-testid="access-app-code">
              <Smartphone className="h-4 w-4" />Add to my club app</button>
            {info.site_url && <button className={`${btn} border dark:border-slate-600`} onClick={() => signIn(true)} disabled={busy} data-testid="access-website">
              <Globe className="h-4 w-4" />Sign in to the club website</button>}
            <p className="text-xs text-slate-500">This device stays signed in. Please don't forward your link.</p>
          </>)}
        </div>
      </div>
    </div>
  );
}
